import * as React from 'react';

import type { ChangeLogView, ParameterView } from '@/api/parameters-client';
import { useParameterChangelog, useUpdateParameter } from '@/api/use-parameters';
import { Button } from '@/components/ui/button';
import {
  buildChangelogCsv,
  changelogCsvFileName,
  formatValue,
  isSensitiveParameter,
} from '@/lib/param-format';
import { SensitiveConfirmDialog } from '@/routes/parameters/sensitive-confirm-dialog';

// =============================================================================
// parameter-changelog.tsx — Historique d'un paramètre + retour à une version (A-24).
// =============================================================================
// GET changelog (D4) rendu en tableau sémantique (<table> + <caption> + scope)
// pour l'accessibilité lecteur d'écran. Aucun code couleur porteur d'information.
//
// §2.8 l.1938 — « Revenir à cette valeur » : depuis une ligne d'historique, on
// ré-applique une valeur ANTÉRIEURE via le MÊME PATCH que l'édition (aucun endpoint
// back nouveau). C'est DISTINCT de « Restaurer la valeur par défaut ». La commande
// est « soumise à la même confirmation que toute modification » : confirmation
// simple pour un paramètre ordinaire, TEXTUELLE (type-to-confirm) s'il est sensible.

export interface ParameterChangelogProps {
  /** Paramètre courant — porte la clé, la valeur en vigueur et la sensibilité. */
  readonly parameter: ParameterView;
}

export function ParameterChangelog({
  parameter,
}: ParameterChangelogProps): React.JSX.Element {
  const { paramKey } = parameter;
  const changelog = useParameterChangelog(paramKey);
  const update = useUpdateParameter();
  const sensitive = isSensitiveParameter(parameter);
  // Valeur d'historique en attente de confirmation de retour (ou null).
  const [pending, setPending] = React.useState<unknown>(null);
  const [hasPending, setHasPending] = React.useState(false);

  if (changelog.isPending) {
    return (
      <p role="status" className="text-sm text-muted-foreground">
        Chargement de l&apos;historique…
      </p>
    );
  }

  if (changelog.isError) {
    return (
      <p role="alert" className="text-sm text-destructive">
        Erreur de chargement de l&apos;historique : {changelog.error.message}
      </p>
    );
  }

  const entries = changelog.data;
  if (entries.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Aucune modification enregistrée pour ce paramètre.
      </p>
    );
  }

  const requestRevert = (value: unknown): void => {
    setPending(value);
    setHasPending(true);
  };
  const cancelRevert = (): void => {
    setHasPending(false);
    setPending(null);
  };
  const confirmRevert = (): void => {
    update.mutate({
      paramKey,
      input: {
        value: pending,
        reason: 'Retour à une valeur antérieure (historique)',
      },
    });
    cancelRevert();
  };

  return (
    <div className="space-y-3">
      <ExportChangelogCsvButton paramKey={paramKey} entries={entries} />
      <div className="overflow-x-auto">
        <table className="ds-table w-full border-collapse text-sm">
          <caption className="sr-only">
            Historique des modifications du paramètre {paramKey}
          </caption>
          <thead>
            <tr className="border-b border-border text-left text-muted-foreground">
              <th scope="col" className="px-3 py-2 font-medium">
                Date
              </th>
              <th scope="col" className="px-3 py-2 font-medium">
                Ancienne valeur
              </th>
              <th scope="col" className="px-3 py-2 font-medium">
                Nouvelle valeur
              </th>
              <th scope="col" className="px-3 py-2 font-medium">
                Auteur
              </th>
              <th scope="col" className="px-3 py-2 font-medium">
                Motif
              </th>
              <th scope="col" className="px-3 py-2 font-medium">
                <span className="sr-only">Retour à cette valeur</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => {
              // « cette valeur » = la valeur EN VIGUEUR après ce changement
              // (état historique réel du paramètre). Le retour est inutile quand
              // elle correspond déjà à la valeur courante (ligne la plus récente).
              const isCurrent = entry.newValue === parameter.value;
              return (
                <tr key={entry.changeId} className="border-b border-border">
                  <td className="px-3 py-2 font-mono text-xs">
                    {entry.changedAt}
                  </td>
                  <td className="px-3 py-2 font-mono">
                    {formatValue(entry.oldValue)}
                  </td>
                  <td className="px-3 py-2 font-mono">
                    {formatValue(entry.newValue)}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {entry.changedBy}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {entry.reason ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {isCurrent ? (
                      <span className="text-xs text-muted-foreground">
                        Valeur courante
                      </span>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={update.isPending}
                        onClick={() => requestRevert(entry.newValue)}
                        aria-label={`Revenir à la valeur ${formatValue(entry.newValue)} du ${entry.changedAt}`}
                      >
                        Revenir à cette valeur
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div aria-live="polite" aria-atomic="true">
        {update.isError ? (
          <p role="alert" className="text-sm text-destructive">
            Retour refusé : {update.error.message}
          </p>
        ) : null}
        {update.isSuccess ? (
          <p role="status" className="text-sm text-foreground">
            Valeur antérieure ré-appliquée.
          </p>
        ) : null}
      </div>

      <SensitiveConfirmDialog
        open={hasPending}
        paramKey={paramKey}
        sensitive={sensitive}
        title="Revenir à une valeur antérieure"
        description={`Vous allez ré-appliquer la valeur « ${formatValue(pending)} » à « ${paramKey} ». Cette valeur sera validée par le serveur.`}
        confirmLabel="Revenir à cette valeur"
        onCancel={cancelRevert}
        onConfirm={confirmRevert}
      />
    </div>
  );
}

interface ExportChangelogCsvButtonProps {
  readonly paramKey: string;
  readonly entries: readonly ChangeLogView[];
}

/**
 * Export CSV de l'historique d'UN paramètre (§2.8 l.1950 « … et de son historique »,
 * V28-UI-COMPLETUDE item 5). Données = `entries` DÉJÀ chargées par
 * `useParameterChangelog` (aucun endpoint back dédié, l'export porte sur le
 * paramètre sélectionné). Même pattern Blob + BOM UTF-8 que `ExportCsvButton`
 * (parameters-screen.tsx) ; sérialisation via `buildChangelogCsv` qui réutilise
 * `csvEscapeCell` (anti-injection-formule OBS-1).
 */
function ExportChangelogCsvButton({
  paramKey,
  entries,
}: ExportChangelogCsvButtonProps): React.JSX.Element {
  const onExport = (): void => {
    const csv = buildChangelogCsv(paramKey, entries);
    // BOM UTF-8 : garantit l'interprétation des accents par Excel (FR).
    const blob = new Blob([`﻿${csv}`], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = changelogCsvFileName(paramKey);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Button type="button" variant="outline" size="sm" onClick={onExport}>
      Exporter l&apos;historique en CSV
    </Button>
  );
}
